import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Upload, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { GALLERY_FILTERS } from '@/lib/site';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errorMessage';

const CATS = GALLERY_FILTERS.filter((f) => f.id !== 'all');
const EMPTY = { image_url: '', title: '', category: 'atelier', order: 0 };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve image src — supports relative /api/uploads/... and absolute URLs
import { resolveImageUrl as resolveImgSrc } from '@/lib/resolveImageUrl';

export default function GalleryPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [filter, setFilter] = useState('all');
    const fileInputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/gallery', { params: filter === 'all' ? {} : { category: filter } });
            setItems(res.data || []);
        } catch { toast.error('Impossibile caricare la galleria'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

    const openNew = () => setEditing({ ...EMPTY, _new: true });
    const openEdit = (it) => setEditing({ ...it });

    const handleFileChosen = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File troppo grande (max 10 MB)');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${BACKEND_URL}/api/admin/gallery/upload`, {
                method: 'POST',
                credentials: 'include', // send httpOnly admin cookie
                body: fd,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || 'Errore upload');
            setEditing((prev) => ({ ...prev, image_url: data.image_url }));
            toast.success('Foto caricata');
        } catch (err) {
            toast.error(typeof err?.message === 'string' ? err.message : 'Errore upload');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const save = async () => {
        if (!editing.image_url || !editing.title || !editing.category) { toast.error('Compila tutti i campi'); return; }
        setSaving(true);
        try {
            const payload = { image_url: editing.image_url, title: editing.title, category: editing.category, order: parseInt(editing.order || 0, 10) };
            if (editing._new) await api.post('/admin/gallery', payload);
            else await api.put(`/admin/gallery/${editing.id}`, payload);
            toast.success(editing._new ? 'Aggiunto alla galleria' : 'Aggiornato');
            setEditing(null);
            load();
        } catch (e) {
            toast.error(errorMessage(e, 'Errore salvataggio'));
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm('Eliminare questa immagine?')) return;
        try { await api.delete(`/admin/gallery/${id}`); toast.success('Eliminato'); load(); }
        catch { toast.error('Errore eliminazione'); }
    };

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={TEST_IDS.adminGalleryTable}>
            <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-serif text-xl">Galleria</h2>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {GALLERY_FILTERS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Aggiungi immagine</Button>
                </div>
            </div>

            <div className="p-5">
                {loading ? (
                    <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento…</div>
                ) : items.length === 0 ? (
                    <div className="py-10 text-center text-muted-foreground">Nessuna immagine in questa categoria.</div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {items.map((it) => (
                            <div key={it.id} className="group rounded-md border border-border overflow-hidden bg-card">
                                <div className="aspect-square overflow-hidden bg-muted">
                                    <img src={resolveImgSrc(it.image_url)} alt={it.title} loading="lazy" className="w-full h-full object-cover" />
                                </div>
                                <div className="p-3">
                                    <div className="text-sm font-medium truncate">{it.title}</div>
                                    <div className="text-xs text-muted-foreground capitalize mt-0.5">{it.category} · ord. {it.order}</div>
                                    <div className="flex gap-2 mt-3">
                                        <Button size="sm" variant="outline" className="h-8 px-3 flex-1" onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5 mr-1.5" />Modifica</Button>
                                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => remove(it.id)}><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-lg">
                    {editing && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl">{editing._new ? 'Nuova immagine' : 'Modifica immagine'}</DialogTitle>
                                <DialogDescription>Carica il file dal tuo computer oppure incolla un link.</DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 mt-2">
                                <div>
                                    <Label className="eyebrow">Foto *</Label>
                                    <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            className="flex-1"
                                            data-testid="admin-gallery-upload-button"
                                        >
                                            {uploading ? (
                                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Caricamento…</>
                                            ) : (
                                                <><Upload className="h-4 w-4 mr-2" />Carica dal computer</>
                                            )}
                                        </Button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                            onChange={handleFileChosen}
                                            className="hidden"
                                            data-testid="admin-gallery-file-input"
                                        />
                                    </div>
                                    <div className="relative my-3 flex items-center gap-3 text-xs text-muted-foreground">
                                        <div className="flex-1 h-px bg-border" />
                                        <span>oppure</span>
                                        <div className="flex-1 h-px bg-border" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                        <Input
                                            value={editing.image_url || ''}
                                            onChange={(ev) => setEditing({ ...editing, image_url: ev.target.value })}
                                            placeholder="Incolla un URL https://…"
                                        />
                                    </div>
                                    {editing.image_url && (
                                        <div className="mt-3 rounded-md border border-border overflow-hidden bg-muted aspect-video">
                                            <img src={resolveImgSrc(editing.image_url)} alt="Anteprima" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    <p className="mt-2 text-xs text-muted-foreground">JPG, PNG, WEBP, GIF · max 10 MB</p>
                                </div>
                                <div>
                                    <Label className="eyebrow">Titolo *</Label>
                                    <Input value={editing.title} onChange={(ev) => setEditing({ ...editing, title: ev.target.value })} className="mt-1.5" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="eyebrow">Categoria *</Label>
                                        <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Ordine</Label>
                                        <Input type="number" value={editing.order} onChange={(ev) => setEditing({ ...editing, order: ev.target.value })} className="mt-1.5" />
                                    </div>
                                </div>
                            </div>
                            <DialogFooter className="mt-4 gap-2">
                                <Button variant="ghost" onClick={() => setEditing(null)}>Annulla</Button>
                                <Button onClick={save} disabled={saving || uploading}>{saving ? 'Salvataggio…' : 'Salva'}</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
